import { useCallback, useState } from 'react';

/**
 * Welche Gruppen der Navigation aufgeklappt sind.
 *
 * **Gespeichert wird im Browser und nicht beim Server** -- dieselbe Erwaegung
 * wie bei der Zeilenhoehe (``lib/density.ts``): Es ist eine Einstellung des
 * Arbeitsplatzes und keine der Person, und eine Zeile im Server, die pro
 * Nutzer festhaelt, welche Menuegruppe er offen mag, waere eine Zeile mehr im
 * Verzeichnis der Verarbeitungstaetigkeiten fuer nichts.
 *
 * **Die Gruppe der aufgerufenen Seite oeffnet sich von selbst.** Das
 * uebernimmt ``reveal`` und nicht eine Sonderregel im Lesen: Waere "offen"
 * als *entweder gespeichert oder aktiv* berechnet, liesse sich die Gruppe, in
 * der man gerade steht, nicht zuklappen -- der Schalter waere da und taete
 * nichts. So darf sie zu, und beim naechsten Sprung hinein geht sie wieder
 * auf.
 *
 * Die Gruppen sind hier Zeichenketten und nicht ``NavigationGroup``: Diese
 * Ebene kennt die Navigation nicht, und die Abhaengigkeit liefe verkehrt
 * herum.
 */
const STORAGE_KEY = 'itscrm.nav.open';

function read(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const value: unknown = JSON.parse(raw);
    // Was im Speicher steht, hat jemand anders geschrieben -- eine frueherer
    // Fassung, ein Werkzeug, eine Hand. Was nicht passt, wird verworfen und
    // nicht ausgelegt.
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    // Ein Browser, der den Speicher verweigert (privates Fenster, gesperrte
    // Website-Daten), darf die Navigation nicht anhalten. Dann beginnt jede
    // Gruppe zugeklappt -- fuer diese Sitzung.
    return [];
  }
}

function write(groups: readonly string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
  } catch {
    // Siehe oben: Die Einstellung gilt dann nur fuer diese Sitzung.
  }
}

export interface NavGroupState {
  isOpen: (group: string) => boolean;
  /** Auf oder zu -- die Wahl des Betrachters. */
  toggle: (group: string) => void;
  /** Aufklappen, weil die aufgerufene Seite darin liegt. Aendert nichts,
   *  wenn die Gruppe schon offen ist; sonst liefe bei jedem Seitenwechsel
   *  ein Schreibvorgang fuer denselben Stand. */
  reveal: (group: string) => void;
}

export function useNavGroups(): NavGroupState {
  const [open, setOpen] = useState<readonly string[]>(read);

  const isOpen = useCallback((group: string) => open.includes(group), [open]);

  const toggle = useCallback((group: string) => {
    setOpen((current) => {
      const next = current.includes(group)
        ? current.filter((item) => item !== group)
        : [...current, group];
      write(next);
      return next;
    });
  }, []);

  const reveal = useCallback((group: string) => {
    setOpen((current) => {
      if (current.includes(group)) return current;
      const next = [...current, group];
      write(next);
      return next;
    });
  }, []);

  return { isOpen, toggle, reveal };
}
